"use client";

import { useMemo } from "react";
import {
  useTable,
  type ColumnSizingState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Table,
  type Updater,
  type ColumnVisibilityState,
} from "@tanstack/react-table";
import type { RowData } from "@tanstack/react-table";
import { resolveColumnIds } from "../columns/column-id";
import { getAvailableClientPage } from "./client-page";
import { MIN_RESIZED_COLUMN_WIDTH, SELECT_COLUMN_ID } from "./grid-metrics";
import { selectColumn } from "./select-column";
import {
  recordGridFeatures,
  type RecordGridFeatures,
} from "./record-grid-features";
import type { RecordColumnDef } from "../column-types";
import type {
  RecordDataSource,
  RecordObjectDef,
  RecordTableState,
} from "../types";

type RenderedWidths = { current: Map<string, number> };

interface GridTableInputs<TData extends RowData> {
  columns: RecordColumnDef<TData, unknown>[];
  columnOrder: string[];
  columnVisibility: ColumnVisibilityState;
  sorting: SortingState;
  pagination: PaginationState;
  pinnedStart: string[];
}

function applied<TValue>(updater: Updater<TValue>, current: TValue): TValue {
  return updater instanceof Function ? updater(current) : updater;
}

function visibilityFor<TData extends RowData>(
  columns: RecordColumnDef<TData, unknown>[],
  hidden: string[],
): ColumnVisibilityState {
  const hiddenIds = new Set(hidden);
  return Object.fromEntries(
    resolveColumnIds(columns).map((id) => [id, !hiddenIds.has(id)]),
  );
}

function availablePage<TData extends RowData>(
  data: RecordDataSource<TData>,
  state: RecordTableState,
): number {
  return data.mode === "client"
    ? getAvailableClientPage(data.rows.length, state.page, state.pageSize)
    : state.page;
}

function useGridTableInputs<TData extends RowData>(
  object: RecordObjectDef<TData>,
  state: RecordTableState,
  data: RecordDataSource<TData>,
  selectable: boolean,
): GridTableInputs<TData> {
  const columnVisibility = useMemo<ColumnVisibilityState>(
    () => visibilityFor(object.columns, state.columns.hidden),
    [state.columns.hidden, object.columns],
  );

  const sorting = useMemo<SortingState>(
    () =>
      state.sort
        ? [{ id: state.sort.field, desc: state.sort.direction === "desc" }]
        : [],
    [state.sort],
  );

  const columns = useMemo(
    () => (selectable ? [selectColumn<TData>(), ...object.columns] : object.columns),
    [object.columns, selectable],
  );

  const columnOrder = useMemo(
    () =>
      selectable
        ? [SELECT_COLUMN_ID, ...state.columns.order]
        : state.columns.order,
    [selectable, state.columns.order],
  );

  const page = availablePage(data, state);
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: page - 1, pageSize: state.pageSize }),
    [page, state.pageSize],
  );

  return {
    columns,
    columnOrder,
    columnVisibility,
    sorting,
    pagination,
    pinnedStart: startPinnedColumns(object.defaultColumns),
  };
}

function startPinnedColumns(defaultColumns: string[]): string[] {
  const [firstColumn] = defaultColumns;
  return firstColumn ? [SELECT_COLUMN_ID, firstColumn] : [SELECT_COLUMN_ID];
}

function gridTableState<TData extends RowData>(
  inputs: GridTableInputs<TData>,
  state: RecordTableState,
  isClientPaginated: boolean,
) {
  return {
    columnVisibility: inputs.columnVisibility,
    columnOrder: inputs.columnOrder,
    sorting: inputs.sorting,
    rowSelection: state.rowSelection,
    columnSizing: state.columns.widths,
    ...(isClientPaginated ? { pagination: inputs.pagination } : {}),
  };
}

function resizedColumnWidths<TData extends RowData>(
  next: ColumnSizingState,
  state: RecordTableState,
  renderedWidths: RenderedWidths,
  table: Table<RecordGridFeatures, TData>,
): ColumnSizingState {
  const widths = { ...next };
  for (const [id, size] of Object.entries(next)) {
    if (state.columns.widths[id] !== undefined) continue;
    const rendered = renderedWidths.current.get(id);
    const declared = table.getColumn(id)?.columnDef.size;
    if (rendered === undefined || declared === undefined) continue;
    widths[id] = Math.max(MIN_RESIZED_COLUMN_WIDTH, rendered + size - declared);
  }
  return widths;
}

function applyClientPagination(
  next: PaginationState,
  state: RecordTableState,
): void {
  if (next.pageSize !== state.pageSize) {
    state.setPageSize(next.pageSize);
  } else if (next.pageIndex !== state.page - 1) {
    state.setPage(next.pageIndex + 1);
  }
}

interface RecordGridTableOptions<TData extends RowData> {
  object: RecordObjectDef<TData>;
  state: RecordTableState;
  data: RecordDataSource<TData>;
  selectable: boolean;
  infiniteScroll: boolean;
  renderedWidths: RenderedWidths;
}

export function useRecordGridTable<TData extends RowData>(
  options: RecordGridTableOptions<TData>,
): Table<RecordGridFeatures, TData> {
  const { object, state, data, selectable, renderedWidths } = options;
  const inputs = useGridTableInputs(object, state, data, selectable);
  const isClientData = data.mode === "client";
  const isClientPaginated =
    isClientData && data.paginate !== false && !options.infiniteScroll;

  const table: Table<RecordGridFeatures, TData> = useTable({
    features: recordGridFeatures,
    data: data.rows,
    columns: inputs.columns,
    state: gridTableState(inputs, state, isClientPaginated),
    getRowId: object.getRowId,
    enableRowSelection: selectable,
    onRowSelectionChange: (updater: Updater<RowSelectionState>) =>
      state.setRowSelection(applied(updater, state.rowSelection)),
    enableColumnResizing: object.capabilities.columnEditing,
    onColumnSizingChange: (updater: Updater<ColumnSizingState>) =>
      state.setColumns({
        ...state.columns,
        widths: resizedColumnWidths(
          applied(updater, state.columns.widths),
          state,
          renderedWidths,
          table,
        ),
      }),
    columnResizeMode: "onEnd",
    manualSorting: !isClientData,
    manualPagination: !isClientPaginated,
    onPaginationChange: isClientPaginated
      ? (updater: Updater<PaginationState>) =>
          applyClientPagination(applied(updater, inputs.pagination), state)
      : undefined,
    initialState: { columnPinning: { start: inputs.pinnedStart, end: [] } },
  });
  return table;
}
